import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { User } from '../../types';
import { RegisterGroupJoin } from '../../types/customerGroups';
import { api } from '../../services/api';
import designDraftStore, { settleWithin } from '../../utils/designDraft';

/** Bejelentkezéskor: a piszkozat-tároló feloldása, és közös gépen a MÁS felhasználók
 *  piszkozatainak törlése (személyes fotók lehetnek bennük). */
const prepareDraftsForUser = (user: User | undefined) => {
  designDraftStore.unlock();
  if (user?.id !== undefined) designDraftStore.clearAllExcept(`${user.id}:`);
};

/** A piszkozatok törlése legfeljebb ennyi ideig tarthatja fel a kijelentkezést. */
const DRAFT_WIPE_TIMEOUT_MS = 1500;

interface AuthState {
  user: User | null;
  /**
   * @deprecated A token authoritative forrása a sessionStorage (lásd `services/api.ts`
   * axios interceptor). Ez a mező csak retrofit kompatibilitásból van itt — ne
   * használd új kódban, hanem `sessionStorage.getItem('token')` vagy a
   * `selectIsAuthenticated` selector.
   */
  token: string | null;
  isAuthenticated: boolean;
  loading: boolean;
  error: string | null;
  /** A regisztrációkor beírt meghívó kód sikertelen csatlakozásának üzenete (a Layout sávja mutatja). */
  inviteNotice?: string | null;
}

// A sessionStorage az igazi forrás — Redux init csak duplikál, hogy az
// `isAuthenticated` selector és UI azonnal lássa a session state-et.
const initialToken = sessionStorage.getItem('token');
const initialState: AuthState = {
  user: null,
  token: initialToken,
  isAuthenticated: !!initialToken,
  loading: false,
  error: null,
  inviteNotice: null,
};

// Async thunks
export const login = createAsyncThunk(
  'auth/login',
  async (credentials: { email: string; password: string }, { rejectWithValue }) => {
    try {
      const response = await api.post('/auth/login/', credentials);
      const { user, access, refresh } = response.data;
      sessionStorage.setItem('token', access);
      // A refresh-token teszi lehetővé a néma access-frissítést (A29) — nélküle
      // a lejáró access mid-session kidobná a felhasználót.
      if (refresh) localStorage.setItem('refresh_token', refresh);
      prepareDraftsForUser(user);
      return { user, token: access };
    } catch (error: any) {
      // Extract error message from API response
      const message = error.response?.data?.non_field_errors?.[0]
        || error.response?.data?.detail
        || error.response?.data?.message
        || error.response?.data?.error
        || 'Hibás email cím vagy jelszó';
      return rejectWithValue(message);
    }
  }
);

export const register = createAsyncThunk(
  'auth/register',
  async (userData: {
    email: string;
    password: string;
    first_name: string;
    last_name: string;
    phone?: string;
    street_address?: string;
    city?: string;
    postal_code?: string;
    country?: string;
    company_name?: string;
    tax_number?: string;
    is_business?: boolean;
    newsletter_subscription?: boolean;
    marketing_consent?: boolean;
    /** Zárt vásárlói csoport meghívó kódja (opcionális). */
    invite_code?: string;
  }, { rejectWithValue }) => {
    try {
      const response = await api.post('/auth/register/', userData);
      const { user, access, refresh, group_join: groupJoin } = response.data as {
        user: User; access: string; refresh?: string; group_join?: RegisterGroupJoin;
      };
      sessionStorage.setItem('token', access);
      if (refresh) localStorage.setItem('refresh_token', refresh);
      prepareDraftsForUser(user);
      // Sikertelen csatlakozásnál (rossz kód / limit) a regisztráció attól még sikeres —
      // a jelzést a Layout sávja mutatja; sikernél az üdvözlő sáv szól.
      return { user, token: access, inviteNotice: groupJoin && !groupJoin.joined ? groupJoin.message : null };
    } catch (error: any) {
      // Extract error message from API response
      const message = error.response?.data?.non_field_errors?.[0]
        || error.response?.data?.detail
        || error.response?.data?.message
        || error.response?.data?.error
        || (error.response?.data?.email && `Email: ${error.response.data.email[0]}`)
        || (error.response?.data?.password && `Jelszó: ${error.response.data.password[0]}`)
        || 'Regisztráció sikertelen';
      return rejectWithValue(message);
    }
  }
);

export const logout = createAsyncThunk(
  'auth/logout',
  async () => {
    // A backend a refresh tokent blacklistre teszi — kötelező elküldeni (eddig
    // üres törzzsel ment, így minden kijelentkezés 400-zal bukott, és a
    // felhasználó bejelentkezve maradt).
    // Adatvédelem: a tervpiszkozatok személyes fotókat is tartalmazhatnak — a törlés a
    // hálózati hívás ELŐTT indul (ha az elnavigál, a törlés már fut). Lejárt
    // munkamenetnél NEM töröljük őket — épp ezért mentjük.
    const wipe = designDraftStore.clearAll();
    const refreshToken = localStorage.getItem('refresh_token');
    try {
      if (refreshToken) {
        // skipAuthRefresh: a 401 ne indítson frissítést/átirányítást — úgyis kijelentkezünk.
        await api.post('/auth/logout/', { refresh_token: refreshToken }, { skipAuthRefresh: true });
      }
    } catch (error) {
      // A szerveroldali tiltólistázás hibája nem tarthatja bent a felhasználót.
      console.error('Kijelentkezés: a refresh token tiltólistázása sikertelen', error);
    } finally {
      sessionStorage.removeItem('token');
      localStorage.removeItem('refresh_token');
      await settleWithin(wipe, DRAFT_WIPE_TIMEOUT_MS);
    }
  }
);

export const getCurrentUser = createAsyncThunk<User, void, { rejectValue: { status?: number } }>(
  'auth/getCurrentUser',
  async (_arg, { rejectWithValue }) => {
    try {
      const response = await api.get('/auth/user/');
      return response.data as User;
    } catch (error) {
      // A státusz kell a reducernek: csak 401 jelent érvénytelen munkamenetet.
      const status = (error as { response?: { status?: number } } | null)?.response?.status;
      return rejectWithValue({ status });
    }
  }
);

export const updateUserProfile = createAsyncThunk(
  'auth/updateUserProfile',
  async (userData: Partial<User>, { rejectWithValue }) => {
    try {
      const response = await api.patch('/auth/user/', userData);
      return response.data as User;
    } catch (err: any) {
      // Backend Hungarian error message → UI-nak átadjuk
      const detail = err?.response?.data?.detail
        || err?.response?.data?.error
        || (err?.response?.data && typeof err.response.data === 'object'
              ? Object.values(err.response.data).flat().join(' · ')
              : null)
        || err?.message
        || 'Profil frissítése sikertelen';
      return rejectWithValue(String(detail));
    }
  }
);

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setToken: (state, action: PayloadAction<string>) => {
      state.token = action.payload;
      state.isAuthenticated = true;
      sessionStorage.setItem('token', action.payload);
    },
    /** Csak EZEN a lapon jelentkeztet ki (állapot + a lap access tokenje); a közös
     *  localStorage refresh-tokenjét nem bántja — több lapos szinkronhoz, ahol az már
     *  törölve van, vagy egy másik lapon belépett felhasználóé. */
    clearInviteNotice: (state) => {
      state.inviteNotice = null;
    },
    clearAuthThisTab: (state) => {
      state.user = null;
      state.token = null;
      state.isAuthenticated = false;
      sessionStorage.removeItem('token');
    },
  },
  extraReducers: (builder) => {
    builder
      // Login
      .addCase(login.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(login.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload.user;
        state.token = action.payload.token;
        state.isAuthenticated = true;
      })
      .addCase(login.rejected, (state, action) => {
        state.loading = false;
        state.error = (action.payload as string) || 'Bejelentkezés sikertelen';
      })
      // Register
      .addCase(register.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(register.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload.user;
        state.token = action.payload.token;
        state.isAuthenticated = true;
        state.inviteNotice = action.payload.inviteNotice;
      })
      .addCase(register.rejected, (state, action) => {
        state.loading = false;
        state.error = (action.payload as string) || 'Regisztráció sikertelen';
      })
      // Logout
      .addCase(logout.fulfilled, (state) => {
        state.user = null;
        state.token = null;
        state.isAuthenticated = false;
        state.inviteNotice = null;
      })
      // Get current user
      .addCase(getCurrentUser.fulfilled, (state, action) => {
        state.user = action.payload;
        state.isAuthenticated = true;
      })
      .addCase(getCurrentUser.rejected, (state, action) => {
        // Csak 401 (érvénytelen munkamenet) jelentkeztet ki — hálózati / 5xx hibánál a
        // tokenek maradnak (különben egy szerverhiba minden lapon kiléptetne).
        if (action.payload?.status !== 401) return;
        // Csak EZ a lap jelentkezik ki: a közös refresh-tokent nem töröljük — egy valóban
        // sikertelen refreshnél azt az interceptor már kezelte (és csak ha még az a tárolt).
        state.user = null;
        state.token = null;
        state.isAuthenticated = false;
        sessionStorage.removeItem('token');
      })
      // Update user profile — FONTOS: NEM állítjuk a global `loading`-ot
      // save alatt, mert a ProfilePage skeleton-ra vált tőle mid-save.
      .addCase(updateUserProfile.pending, (state) => {
        state.error = null;
      })
      .addCase(updateUserProfile.fulfilled, (state, action) => {
        state.user = action.payload;
      })
      .addCase(updateUserProfile.rejected, (state, action) => {
        state.error = (action.payload as string) || action.error.message || 'Profile update failed';
      });
  },
});

export const { clearError, setToken, clearAuthThisTab, clearInviteNotice } = authSlice.actions;
export default authSlice.reducer;